import {
  Form,
  Input,
  Button,
  Typography,
  Carousel,
  Modal,
  Spin,
  Alert
} from "antd";
import FormItem from "antd/lib/form/FormItem";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { ErrorMessages, SentryErrorSources } from "constants/index";
import { AppLogoIcon } from "components/svg-icons";
import PiiConsent from "components/widgets/pii-consent";
import {
  clearIsSSOLogin,
  getAccessToken,
  getIsSSOLogin,
  peekAuthenticatedAuthRouteRedirect,
  resolveAuthenticatedAuthRouteRedirect,
  ssoLogin
} from "services/auth";
import { SSOLoginRequest } from "models/user";
import { LoadingOutlined } from "@ant-design/icons";
import AuthRedirectScreen from "components/auth-redirect-screen";
import ErrorManager from "utils/cumulative-error-message/error-manager";
import { useAutoFocusFirstField } from "hooks/useAutoFocusFirstField";
import {
  getGenericPostLoginRedirectUrl,
  getProjectInviteRedirectUrlFromParams,
  hasPendingPostLoginRedirect,
  persistSsoPostLoginRedirect
} from "utils/project-invite-redirect";

const { Title } = Typography;

function SSOLogin() {
  const history = useHistory();
  const location = useLocation();
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  useAutoFocusFirstField(formContainerRef, { page: true });

  const [loader, setLoader] = useState(false);
  const [isErrorOnValidation, setIsErrorOnValidation] = useState(false);
  const [message, setMessage] = useState("");
  const [loginForm] = Form.useForm();

  const slider: any = useRef();

  useEffect(() => {
    const search =
      typeof window !== "undefined" && window.location
        ? window.location.search
        : "";
    const params = new URLSearchParams(search);
    const error = params.get("error");
    const email = params.get("email");
    loginForm.setFieldsValue({ email });

    if (error) {
      setIsErrorOnValidation(true);
      setMessage(error);
    }
  }, [location.hash, loginForm]);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const buildLoginRedirectPath = useCallback(
    (emailParam: string) => {
      const inviteRedirect = getProjectInviteRedirectUrlFromParams(
        location.search,
        location.hash
      );
      const redirectUrl =
        inviteRedirect || getGenericPostLoginRedirectUrl(location.search);
      const loginPath = `/Login?email=${encodeURIComponent(emailParam)}`;
      if (!redirectUrl) {
        return loginPath;
      }
      return `${loginPath}&redirectUrl=${encodeURIComponent(redirectUrl)}`;
    },
    [location.search, location.hash]
  );

  const hasPostLoginRedirect = useMemo(
    () => hasPendingPostLoginRedirect(location.search, location.hash),
    [location.search, location.hash]
  );

  const authenticatedRedirectPath = useMemo(
    () =>
      hasPostLoginRedirect
        ? peekAuthenticatedAuthRouteRedirect(location.search, location.hash)
        : null,
    [hasPostLoginRedirect, location.hash, location.search]
  );

  useEffect(() => {
    if (searchParams.get("code") && location.pathname !== "/callback") {
      history.replace({ pathname: "/callback", search: location.search });
      return;
    }

    if (!hasPostLoginRedirect) {
      if (!getAccessToken() && getIsSSOLogin()) {
        clearIsSSOLogin();
      }
      return;
    }

    const redirectPath = resolveAuthenticatedAuthRouteRedirect(
      location.search,
      location.hash
    );
    if (redirectPath) {
      history.replace(redirectPath);
      return;
    }

    if (!getAccessToken() && getIsSSOLogin()) {
      clearIsSSOLogin();
    }
  }, [
    hasPostLoginRedirect,
    history,
    location.hash,
    location.pathname,
    location.search,
    searchParams
  ]);

  const onLoginFinish = useCallback(
    async (event: SSOLoginRequest) => {
      const res: any = await ssoLogin({
        email: event.email
      });

      if (res.status === 200) {
        setLoader(true);
        if (
          res.data.message === "SSO not configured" ||
          res.data.message === "No SSO provider associated with this user"
        ) {
          setLoader(false);
          history.push(buildLoginRedirectPath(event.email));
        } else {
          const postLoginRedirect =
            getProjectInviteRedirectUrlFromParams(
              location.search,
              location.hash
            ) || getGenericPostLoginRedirectUrl(location.search);
          if (postLoginRedirect) {
            persistSsoPostLoginRedirect(postLoginRedirect);
          }
          setTimeout(() => {
            try {
              if (typeof window !== "undefined" && window.location) {
                window.location.href = res.data.loginUrl;
              }
              setTimeout(() => {
                setLoader(false);
              }, 5000);
            } catch (error: any) {
              setLoader(false);
              Modal.error({
                content:
                  error?.response?.data?.error ||
                  error?.message ||
                  "Unable to start SSO sign-in. Please try again."
              });
              ErrorManager().handleError({
                error,
                showError: false,
                errorSource: SentryErrorSources.auth
              });
            }
          }, 1000);
        }
      } else if (res.response?.status === 403) {
        setLoader(false);
        history.push(
          `/account-locked?email=${encodeURIComponent(event.email)}`
        );
      } else {
        setLoader(false);
      }
    },
    [buildLoginRedirectPath, history, location.hash, location.search]
  );

  if (authenticatedRedirectPath) {
    return <AuthRedirectScreen />;
  }

  return (
    <div className="login">
      <div className="login-form">
        <div style={{ margin: "auto", display: "block" }}>
          <AppLogoIcon />
        </div>
        <div className="login-logo">
          <Title level={3} className="mt-3">
            Login
          </Title>
        </div>
        <Carousel
          dots={false}
          afterChange={() => {}}
          ref={(ref) => {
            slider.current = ref;
          }}
          speed={250}
          infinite={false}
          swipe={false}
          swipeToSlide={false}
          draggable={false}
        >
          <div className="px-1" ref={formContainerRef}>
            <Form
              onFinish={onLoginFinish}
              form={loginForm}
              labelCol={{ span: 50 }}
              wrapperCol={{ span: 30 }}
              scrollToFirstError
              layout="vertical"
            >
              <FormItem
                name="email"
                label="Email"
                layout="vertical"
                rules={[
                  {
                    required: true,
                    validateTrigger: "onSubmit",
                    message: ErrorMessages.EmailRequired
                  },
                  {
                    type: "email",
                    validateTrigger: "onSubmit",
                    message: ErrorMessages.EmailRequired
                  }
                ]}
              >
                <Input placeholder="Enter Email" data-autofocus="true" />
              </FormItem>
              <div className="pt-4">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  disabled={loader}
                  className="w-full"
                >
                  Continue
                </Button>
              </div>
              {loader && (
                <div className="pt-[15px]">
                  <Spin
                    indicator={<LoadingOutlined spin />}
                    size="small"
                    className="pr-[5px]"
                  />
                  Redirecting to your organization&apos;s Single Sign On
                </div>
              )}
              {isErrorOnValidation && (
                <div className="pt-[15px]">
                  <Alert
                    data-testid="sso-login-error-alert"
                    // antd v6 renamed alert internals (ant-alert-message -> ant-alert-title); add a stable testid hook for UI automation
                    title={
                      <span data-testid="sso-login-error-alert-message">
                        {message}
                      </span>
                    }
                    type="error"
                    className="text-xs mt-1 mb-2 rounded-lg"
                    showIcon
                    action={
                      <a
                        href="/forgot_password"
                        className="text-gray-800 hover:text-gray-800 underline hover:underline"
                      >
                        Forgot password?
                      </a>
                    }
                  />
                </div>
              )}
            </Form>
          </div>
        </Carousel>
      </div>
      <PiiConsent className="w-[350px] text-center text-xs text-ciq-gray-600 pt-7" />
    </div>
  );
}

export default SSOLogin;
